<template>
  <div class="row">
    <div class="col-12 px-0">
      <h1 class="d-flex align-items-center">
        <span class="icon mr-2 icon-settings_profil_verwalten" aria-hidden="true"></span>
        {{ $t('views.reservationEdit.title') }}
      </h1>
    </div>

    <button-back @clicked="back()" />

    <loading-container :state="state" errorLinkTo="/reservations">
      <dl>
        <dt>{{ $t('common.ticketTime') }}</dt>
        <dd>{{ ticketTime() }}</dd>
        <dt>{{ $t('views.reservationEdit.status') }}</dt>
        <dd>{{ $t('common.ticketTypeSingle.' + ticket.status) }}</dd>
      </dl>

      <div class="row mx-auto mt-4 mb-4" v-if="showDeleteConfirmation">
        <div class="col-12 font-weight-bold px-0 mb-3">
          {{ $tc('views.reservationEdit.confirmDelete', { count: relatedTicketsAvailable().length + 1, msg: surName + ' ' + givenName}) }}
        </div>
        <div class="col-12 admin-delete-checkboxes mb-2">
          <control-checkboxes
            :available="relatedTicketsAvailable()"
            v-model="relatedTicketsSelected"
          />
        </div>
        <div class="col-12 col-md-4 offset-md-2 col-lg-3 offset-lg-3 mt-1 px-xs-0">
          <button-cancel v-on:clicked="showDeleteConfirmation = false" class="btn-w-100" />
        </div>
        <div class="col-12 col-md-4 col-lg-3 mt-1 px-xs-0">
          <button-delete i18nMessage="common.ticketCancel" v-on:clicked="cancelTickets()" class="btn-w-100" />
        </div>
      </div>

      <form @submit.prevent="onSubmit()" v-if="!showDeleteConfirmation">
        <control-select
          i18nLabel="common.owner.salutation"
          :validation="v$.salutation"
          v-model="salutation"
          :availableValues="salutationOptions()"
        />
        <control-input
          i18nLabel="common.owner.title"
          :validation="v$.title"
          v-model="title"
        />
        <control-input
          i18nLabel="common.owner.givenName"
          :validation="v$.givenName"
          v-model="givenName"
        />
        <control-input
          i18nLabel="common.owner.surName"
          :validation="v$.surName"
          v-model="surName"
        />
        <control-input
          i18nLabel="common.owner.organization"
          :validation="v$.organization"
          v-model="organization"
        />
        <control-input
          i18nLabel="common.owner.email"
          :validation="v$.email"
          v-model="email"
          type="email"
        />
        <control-input
          i18nLabel="common.owner.phone"
          :validation="v$.phone"
          v-model="phone"
          type="tel"
        />
        <control-checkbox
          i18nLabel="common.owner.accessible"
          :validation="v$.accessible"
          v-model="accessible"
        />

        <div class="row mx-auto mt-4">
          <div class="col-12 col-md-4 offset-md-2 col-lg-3 offset-lg-3 mt-1 px-xs-0" v-if="ticket.cancelable">
            <button-delete i18nMessage="common.ticketCancel" v-on:click.prevent="showDeleteConfirmation = true" class="btn-w-100" />
          </div>
          <div class="col-12 col-md-4 col-lg-3 mt-1 px-xs-0">
            <button-save v-on:click.prevent="saveTicket()" class="btn-w-100" />
          </div>
        </div>

      </form>
    </loading-container>
  </div>
</template>

<script lang="ts">
import ControlCheckbox from '@/components/controls/ControlCheckbox.vue';
import ControlInput from '@/components/controls/ControlInput.vue';
import ControlSelect from '@/components/controls/ControlSelect.vue';
import ButtonBack from '@/components/interaction/ButtonBack.vue';
import ButtonDelete from '@/components/interaction/ButtonDelete.vue';
import ButtonCancel from '@/components/interaction/ButtonCancel.vue';
import ButtonSave from '@/components/interaction/ButtonSave.vue';
import LoadingContainer from '@/components/structure/LoadingContainer.vue';
import { Ticket, TicketOwner, TicketOwnerSalutationEnum, TicketSalutationEnum } from '@/generated/openapi';
import LoadingMixin from '@/mixins/LoadingMixin';
import { SelectOption } from '@/models/SelectOption';
import { adminService } from '@/services/adminService';
import { formatTicketTime } from '@/utils/formatUtils';
import { createSalutationOptions } from '@/utils/formUtils';
import { LoadingState } from '@/utils/loadingUtils';
import { smartBack, urlParamString } from '@/utils/navigationUtils';
import { isTelephoneString } from '@/utils/validationUtils';
import useVuelidate from '@vuelidate/core';
import { email, maxLength, required } from '@vuelidate/validators';
import { defineComponent } from 'vue';
import ControlCheckboxes, { ControlCheckboxesOption } from '@/components/controls/ControlCheckboxes.vue';
import { InfoPopupBase, InfoPopupType } from '@/models/InfoPopupData';

export default defineComponent({
  name: 'ReservationEdit',
  mixins: [
    LoadingMixin
  ],
  data() {
    return {
      ticket: {} as Ticket,
      salutation: TicketSalutationEnum.None,
      title: '',
      givenName: '',
      surName: '',
      organization: '',
      email: '',
      phone: '',
      accessible: false,
      showDeleteConfirmation: false,
      relatedTickets: [] as Ticket[],
      relatedTicketsSelected: [] as string[],
    }
  },
  components: {
    LoadingContainer,
    ControlInput,
    ControlSelect,
    ControlCheckbox,
    ButtonSave,
    ButtonDelete,
    ButtonCancel,
    ButtonBack,
    ControlCheckboxes,
  },
  created() {
    const ticketId = urlParamString(this.$route, 'id')
    adminService.getTicket(ticketId).then(resp => {
      const ticket = resp.data
      this.ticket = ticket
      this.salutation = ticket.salutation || TicketSalutationEnum.None
      this.title = ticket.title || ''
      this.givenName = ticket.firstName || ''
      this.surName = ticket.lastName || ''
      this.organization = ticket.organisation || ''
      this.email = ticket.email || ''
      this.phone = ticket.phone || ''
      this.accessible = ticket.accessibility || false
      return adminService.relatedTickets(ticket.ownerId || '', ticket.id)
    }).then(relatedTickets => {
      this.relatedTickets = relatedTickets
      this.state = LoadingState.success
    }).catch(this.handleError)
  },
  setup() {
    return {
      v$: useVuelidate()
    }
  },
  validations() {
    return {
      salutation: {
      },
      title: {
        maxLength: maxLength(40)
      },
      givenName: {
        required,
        maxLength: maxLength(40)
      },
      surName: {
        required,
        maxLength: maxLength(40)
      },
      organization: {
        maxLength: maxLength(40)
      },
      email: {
        email,
        maxLength: maxLength(320)
      },
      phone: {
        isTelephoneString,
        maxLength: maxLength(40)
      },
      accessible: {
      },
    }
  },
  methods: {
    relatedTicketsAvailable(): ControlCheckboxesOption[] {
      return this.relatedTickets
        .filter(ticket => !!ticket.cancelable)
        .map(ticket => {
          return {
            value: ticket.id,
            label: ticket.roomName + ': ' + formatTicketTime(ticket)
          }
        })
    },
    ticketTime() {
      return formatTicketTime(this.ticket)
    },
    status() {
      return this.ticket.status
    },
    salutationOptions(): SelectOption[] {
      return createSalutationOptions()
    },
    confirmDeleteParams() {
      return [
        this.ticket.firstName + " " + this.ticket.lastName
      ]
    },
    cancelTickets() {
      this.state = LoadingState.loading
      const affectedTickets = [this.ticket.id].concat(this.relatedTicketsSelected)
      adminService.cancelTicket(affectedTickets)
        .then(() => {
          this.$store.commit('addInfoPopup', {
            title: this.$tc('views.reservationEdit.ticketsCancelled', { count: affectedTickets.length } ),
            type: InfoPopupType.info
          } as InfoPopupBase)
          this.back()
        })
        .catch(this.handleErrorWithI18nString)
    },
    asSalutation(): TicketOwnerSalutationEnum {
      if (this.salutation === TicketSalutationEnum.Mr) {
        return TicketOwnerSalutationEnum.Mr;
      } else if(this.salutation === TicketSalutationEnum.Ms) {
        return TicketOwnerSalutationEnum.Ms;
      }
      return TicketOwnerSalutationEnum.None
    },
    asTicketOwner(): TicketOwner {
      return {
        salutation: this.asSalutation(),
        title: this.title,
        firstName: this.givenName,
        lastName: this.surName,
        organisation: this.organization,
        email: this.email,
        phone: this.phone,
        accessibility: this.accessible,
      }
    },
    saveTicket() {
      this.state = LoadingState.loading
      adminService.updateTicket(this.ticket.id, this.asTicketOwner())
        .then(() => {
          this.back()
        })
        .catch(this.handleError)
    },
    back() {

      smartBack(this.$router,{
      name: 'reservations',
        params: {
          fromSearch: 'y'
        }
      })

      // smartBack(this.$router, '/reservations')
    }
  }
});
</script>

<style scoped>
dt {
  float: left;
  font-weight: bold;
  margin-right: 0.5em;
}
dd::after {
  clear: left;
  content: " ";
  display: block;
}
@media screen and (max-width: 420px){
  dt{
    width: 80%;
  }
  dd{
    width: 80%;
  }
}
</style>
